<template>
  <div class="mb-3">

    <div class="table-responsive">
      <table class="table table-striped table-hover align-middle mb-0">
        <thead>
          <tr>
            <th>OFX</th>
            <th>Data</th>
            <th>Tipo mov.</th>
            <th class="text-end">Valor</th>
            <th>Classificação</th>
            <th>Memo</th>
            <th>Status</th>
            <th>Vínculo</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!items.length">
            <td colspan="9" class="text-center text-muted py-4">Nenhum item encontrado.</td>
          </tr>

          <tr
            v-for="(item, index) in items"
            :key="getBancoItemKey(item, index)"
          >
            <td>
              <span
                class="identificador-item"
                :title="`Item OFX #${item.id}`"
              >
                #{{ item.id }}
              </span>
            </td>          
            <td>{{ formatDate(item.dt_movimento) }}</td>

            <td :class="valorClass(item)">
              {{ formatTipoMovimento(item.tipo_movimento) }}
            </td>
            <td class="text-end" :class="valorClass(item)">
              {{ formatCurrency(item.valor) }}
            </td>

            <td>
              <span class="status-badge" :class="tipoBancarioClass(item.tipo_bancario)">
                {{ formatTipoBancario(item.tipo_bancario) }}
              </span>
            </td>

            <td>{{ item.memo || '—' }}</td>

            <td>
              <span class="status-badge" :class="statusBadgeClass(item)">
                {{ statusLabel(item) }}
              </span>

              <small
                v-if="item.motivo_pendencia"
                class="pendencia-motivo d-block mt-1"
              >
                {{ item.motivo_pendencia }}
              </small>
            </td>

            <td>
              <template v-if="item.movimento_label_amigavel">
                <span>{{ item.movimento_label_amigavel }}</span>
                <small
                  v-if="String(item.movimento_label_amigavel || '').toLowerCase().includes('despesa')"
                  class="text-muted d-block"
                >
                  Valor efetivamente pago
                </small>
              </template>

              <span v-else-if="item.banco_operadora_vinculo_id">
                {{ item.banco_operadora_operadora_nome || 'Operadora' }}
                <template v-if="item.banco_operadora_operadora_item_id">
                  #{{ item.banco_operadora_operadora_item_id }}
                </template>
              </span>

              <span v-else>
                —
              </span>
            </td>

            <td class="table-action-cell">
              <template
                v-if="
                  !item.banco_operadora_vinculo_id &&
                  item.status_conciliacao !== 'CONCILIADO_AUTOMATICO' &&
                  item.status_conciliacao !== 'CONCILIADO_MANUAL' &&
                  item.tipo_pendencia !== 'AGUARDANDO_CONCILIACAO_BANCO_OPERADORA'
                "
              >
                <i
                  class="bi bi-link-45deg text-primary mx-1 action-icon"
                  title="Conciliar"
                  @click="$emit('conciliar', { origemTipo: 'OFX', item })"
                ></i>
              </template>

              <template
                v-else-if="
                  !item.banco_operadora_vinculo_id &&
                  item.tipo_pendencia === 'AGUARDANDO_CONCILIACAO_BANCO_OPERADORA'
                "
              >
                <i
                  class="bi bi-arrow-left-right text-primary mx-1 action-icon"
                  title="Conciliar Banco x Operadora"
                  @click="$emit('conciliar-banco-operadora', { item })"
                ></i>
              </template>

              <template v-else-if="item.banco_operadora_vinculo_id">
                <i
                  class="bi bi-eye-fill text-primary mx-1 action-icon"
                  title="Detalhar"
                  @click="$emit('detalhar-banco-operadora', { item })"
                ></i>

                <i
                  class="bi bi-arrow-counterclockwise text-danger mx-1 action-icon"
                  title="Desfazer"
                  @click="$emit('desfazer-banco-operadora', { item })"
                ></i>
              </template>

              <template v-else>
                <i
                  class="bi bi-eye-fill text-primary mx-1 action-icon"
                  title="Detalhar"
                  @click="$emit('detalhar', { origemTipo: 'OFX', item })"
                ></i>

                <i
                  class="bi bi-arrow-counterclockwise text-danger mx-1 action-icon"
                  title="Desfazer"
                  @click="$emit('desfazer', { origemTipo: 'OFX', item })"
                ></i>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination" class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ pagination.total || 0 }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <select
          class="form-select form-select-sm"
          style="width: 6rem"
          :value="pagination.limit || 10"
          @change="emit('change-limit', Number($event.target.value))"
        >
          <option v-for="n in pageOptions" :key="n" :value="n">
            {{ n }} / página
          </option>
        </select>
      </div>

      <div class="d-flex align-items-center">
        <BPagination
          :model-value="pagination.page || 1"
          :per-page="pagination.limit || 10"
          :total-rows="pagination.total || 0"
          size="sm"
          limit="5"
          first-text="«"
          prev-text="‹"
          next-text="›"
          last-text="»"
          @update:model-value="emit('change-page', $event)"
        />
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed } from 'vue'
import { BPagination } from 'bootstrap-vue-next'

const props = defineProps({
  items: { type: Array, default: () => [] },
  pagination: { type: Object, default: () => ({}) },
  totalLabel: { type: String, default: '' }
})

const emit = defineEmits([
  'conciliar',
  'conciliar-banco-operadora',
  'desfazer',
  'desfazer-banco-operadora',
  'detalhar',
  'detalhar-banco-operadora',
  'change-page',
  'change-limit'
])

const pageOptions = [10, 15, 20, 25, 50]

function getBancoItemKey(item, index) {
  return [
    item?.id || 'sem-id',
    item?.fitid || 'sem-fitid',
    item?.vinculo_id || 'sem-vinculo',
    item?.banco_operadora_vinculo_id || 'sem-operadora',
    item?.status_conciliacao || 'sem-status',
    index
  ].join('-')
}

const startRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return (page - 1) * limit + 1
})

const endRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return Math.min(page * limit, total)
})

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}

function valorClass(item) {
  const tipo = String(item?.tipo_movimento || '').toUpperCase().trim()

  if (tipo === 'DEBIT' || tipo === 'DEBITO' || tipo === 'DÉBITO') {
    return 'valor-debito'
  }

  if (tipo === 'CREDIT' || tipo === 'CREDITO' || tipo === 'CRÉDITO') {
    return 'valor-credito'
  }

  return ''
}

function formatTipoMovimento(v) {
  const tipo = String(v || '').toUpperCase().trim()

  if (tipo === 'DEBIT') return 'Débito'
  if (tipo === 'CREDIT') return 'Crédito'

  return v || '—'
}

function normalizeStatus(v) {
  const map = {
    PENDENTE: 'Pendente de revisão',
    CONCILIADO_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_MANUAL: 'Conciliado manual',
    PENDENTE_AJUSTE: 'Pendente ajuste',
    IGNORADO: 'Ignorado',
    DIVERGENTE: 'Divergente',

    AGUARDANDO_CONCILIACAO_BANCO_OPERADORA: 'Aguardando conciliação Banco x Operadora',
    PAGAMENTO_CONSOLIDADO: 'Pagamento consolidado',
    DESPESA_BANCARIA: 'Despesa bancária',
    PENDENTE_SISTEMA: 'Pendente de revisão',

    CONCILIADO_BANCO_OPERADORA_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_BANCO_OPERADORA_MANUAL: 'Conciliado manual',

    PIX_RECEBIDO: 'Pix recebido',
    PIX_ENVIADO: 'Pix enviado',
    CARTAO_GETNET_CREDITO: 'Getnet crédito',
    CARTAO_GETNET_DEBITO: 'Getnet débito',
    CHEQUE_DEPOSITO: 'Cheque depósito',
    CHEQUE_DEVOLVIDO: 'Cheque devolvido',
    BOLETO: 'Boleto',
    DARF: 'DARF',
    APLICACAO: 'Aplicação',
    RESGATE_APLICACAO: 'Resgate aplicação',
    SALARIOS: 'Salários',
    TARIFA_BANCARIA: 'Tarifa bancária',
    TRANSFERENCIA_BANCARIA: 'Transferência bancária',
    TRANSFERENCIA_INTERNA: 'Transferência interna',
    DEBITO_AUTOMATICO: 'Débito automático',
    OUTRO: 'Outro',

    SEM_LANCAMENTO_SISTEMA: 'Sem lançamento no sistema',
    AUTORIZACAO_DIVERGENTE: 'Autorização divergente',
    POSSIVEL_LANCAMENTO_CONSOLIDADO: 'Possível lançamento consolidado',
    AMBIGUO: 'Ambíguo'
  }

  const key = String(v || '').toUpperCase()
  return map[key] || String(v || '').replaceAll('_', ' ')
}

function statusLabel(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()
  const situacaoBancoOperadora = String(item?.situacao_banco_operadora || '').toUpperCase()

  if (situacaoBancoOperadora) {
    return normalizeStatus(situacaoBancoOperadora)
  }

  if (status.includes('CONCILIADO')) {
    return normalizeStatus(status)
  }

  if (status === 'PENDENTE' && item?.tipo_pendencia) {
    return normalizeStatus(item.tipo_pendencia)
  }

  return normalizeStatus(status || 'PENDENTE')
}

function statusBadgeClass(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()
  const pendencia = String(item?.tipo_pendencia || '').toUpperCase()
  const situacaoBancoOperadora = String(item?.situacao_banco_operadora || '').toUpperCase()

  if (situacaoBancoOperadora === 'CONCILIADO_BANCO_OPERADORA_AUTOMATICO') return 'is-green'
  if (situacaoBancoOperadora === 'CONCILIADO_BANCO_OPERADORA_MANUAL') return 'is-blue'

  if (status.includes('AUTOMATICO')) return 'is-green'
  if (status.includes('MANUAL')) return 'is-blue'
  if (status.includes('AJUSTE')) return 'is-amber'
  if (status.includes('IGNORADO')) return 'is-gray'

  if (pendencia === 'AGUARDANDO_CONCILIACAO_BANCO_OPERADORA') return 'is-purple'
  if (pendencia === 'PAGAMENTO_CONSOLIDADO') return 'is-slate'
  if (pendencia === 'TRANSFERENCIA_INTERNA') return 'is-slate'
  if (pendencia === 'DESPESA_BANCARIA') return 'is-gray'
  if (pendencia === 'POSSIVEL_LANCAMENTO_CONSOLIDADO') return 'is-amber'

  return 'is-yellow'
}

function formatTipoBancario(v) {
  const key = String(v || '').toUpperCase()

  const map = {
    PIX_RECEBIDO: 'Pix recebido',
    PIX_ENVIADO: 'Pix enviado',
    CARTAO_GETNET_CREDITO: 'Getnet crédito',
    CARTAO_GETNET_DEBITO: 'Getnet débito',
    CHEQUE_DEPOSITO: 'Cheque depósito',
    CHEQUE_DEVOLVIDO: 'Cheque devolvido',
    CHEQUE_REAPRESENTADO: 'Cheque reapresentado',
    BOLETO: 'Boleto',
    DARF: 'DARF',
    APLICACAO: 'Aplicação',
    RESGATE_APLICACAO: 'Resgate aplicação',
    SALARIOS: 'Salários',
    TARIFA_BANCARIA: 'Tarifa bancária',
    TRANSFERENCIA_BANCARIA: 'TED/DOC',
    TRANSFERENCIA_INTERNA: 'Transferência interna',
    DEBITO_AUTOMATICO: 'Débito automático',
    OUTRO: 'Outro'
  }

  return map[key] || v || 'Outro'
}

function tipoBancarioClass(v) {
  switch (v) {
    case 'PIX_RECEBIDO':
    case 'PIX_ENVIADO':
      return 'is-blue'

    case 'CARTAO_GETNET_CREDITO':
    case 'CARTAO_GETNET_DEBITO':
      return 'is-purple'

    case 'CHEQUE_DEPOSITO':
    case 'CHEQUE_DEVOLVIDO':
    case 'CHEQUE_REAPRESENTADO':
      return 'is-amber'

    case 'BOLETO':
    case 'DARF':
    case 'APLICACAO':
    case 'RESGATE_APLICACAO':
    case 'TRANSFERENCIA_BANCARIA':
    case 'TRANSFERENCIA_INTERNA':
    case 'DEBITO_AUTOMATICO':
      return 'is-gray'

    case 'SALARIOS':
    case 'TARIFA_BANCARIA':
      return 'is-slate'

    default:
      return 'is-slate'
  }
}
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.table th {
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  font-size: 0.83rem;
  vertical-align: middle;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.status-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
}

.table-action-cell {
  text-align: right !important;
  vertical-align: middle !important;
  white-space: nowrap;
}

.action-icon {
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.15s ease;
}

.action-icon:hover {
  opacity: 0.75;
}

.valor-debito {
  color: #b91c1c;
}

.valor-credito {
  color: #166534;
}

.pendencia-motivo {
  max-width: 260px;
  color: #6b7280;
  font-size: 0.72rem;
  line-height: 1.25;
}

.identificador-item {
  display: inline-block;
  min-width: 52px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

:deep(.pagination.pagination-sm .page-link) {
  font-size: 0.8rem;
  line-height: 1;
  border-radius: 0 !important;
}

:deep(.pagination.pagination-sm) {
  margin-bottom: 0;
}

:deep(.pagination.pagination-sm .page-item.active .page-link) {
  z-index: 1;
}

.is-green { background: #dcfce7; color: #166534; }
.is-blue { background: #dbeafe; color: #1d4ed8; }
.is-yellow { background: #fef3c7; color: #92400e; }
.is-amber { background: #fde68a; color: #92400e; }
.is-gray { background: #e5e7eb; color: #374151; }
.is-purple { background: #ede9fe; color: #6d28d9; }
.is-slate { background: #e2e8f0; color: #334155; }
</style>